// src/pages/cart.jsx
import { Link, Navigate } from "react-router-dom";
import { useCart } from "../CartContext";
import TopSection from "../components/topSection";
import BG from "../assets/shop_bg.png";
import { useEffect, useMemo, useState } from "react";
import { getData } from "../api"; // adjust path if your api.js lives elsewhere


const formatLKR = (n) =>
  `Rs. ${Number(n || 0).toLocaleString("en-LK", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;


export default function CartPage() {
  const { cart, inc, dec, removeFromCart, subtotal, getId } = useCart();

  // ---- Shipping estimator state ----
  const [country, setCountry] = useState("");
  const [city, setCity] = useState("");
  const [zip, setZip] = useState("");
  const [shipping, setShipping] = useState(0);
  const [shippingRule, setShippingRule] = useState("");
  const [shipLoading, setShipLoading] = useState(false);
  const [shipError, setShipError] = useState("");

  const subTotalAmount = useMemo(() => Number(subtotal ?? 0), [subtotal]);
  const grandTotal = useMemo(() => subTotalAmount + Number(shipping || 0), [subTotalAmount, shipping]);

  // Fetch shipping when country & city are available
useEffect(() => {
  const canQuote = country.trim() && city.trim();
  if (!canQuote) return;

  let ignore = false;

  (async () => {
    try {
      setShipError("");
      setShipLoading(true);

      // api.js already has baseURL = http://localhost:3000/api/v1/
      const qs = `country=${encodeURIComponent(country.trim())}&city=${encodeURIComponent(city.trim())}`;
      const data = await getData(`shipping/quote?${qs}`); // -> { currency, amount, rule }

      if (ignore) return;
      setShipping(Number(data?.amount ?? 0));
      setShippingRule(String(data?.rule ?? ""));
    } catch (e) {
      if (!ignore) return;
      setShipError("Could not get a shipping quote. Please check country/city and try again.");
      setShipping(0);
      setShippingRule("");
    } finally {
      if (!ignore) setShipLoading(false);
    }
  })();

  return () => {
    ignore = true;
  };
}, [country, city]);


  if (!cart.length) {
    return (
      <>
      <TopSection
        text="My Cart"
        image={BG}
        backgroundPositionY={"0%"}
      />
      <div className="container py-5" style={{minHeight: "50vh", display: "grid", justifyContent: "center", alignItems: "center"}}>
        <div style={{display: "grid" , justifyContent: "center"}}>
          <h2 className="mb-4">Your cart is empty.</h2>
        <Link className="btn btn-outline-secondary" to="/shop">
          Continue Shopping
        </Link>
        </div>
      </div>
      </>
      
    );
  }

  return (
    <>
      <TopSection
        text="My Cart"
        image={BG}
        backgroundPositionY={"0%"}
      />
      <div className="container py-5" >
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2 className="m-0"></h2>
          <div className="d-flex gap-2">
            <Link className="btn btn-outline-secondary" to="/shop">
              Continue Shopping
            </Link>
          </div>
        </div>

        {/* Cart lines */}
        <div className="table-responsive mb-4">
          <div className="row m-0 p-0">
            <div className="col-2"></div>
            <div className="col">Product</div>
            <div className="col">Price</div>
            <div className="col">Quantity</div>
            <div className="col">Total</div>
            <div className="col-1"></div>
          </div>
          {cart.map((item) => {
            const id = getId(item);
            const line = Number(item.price || 0) * (item.qty ?? 1);
            return (
              <div className="row  m-0 p-0 mt-4" key={id}>
                <div className="col-2">
                  <img
                    src={
                      item.images?.[1] ||
                      "https://via.placeholder.com/70x70?text=No+Image"
                    }
                    alt={item.name ?? "Product"}
                    style={{ objectFit: "cover", borderRadius: 8, width: "70%", aspectRatio: "1/1" }}
                  />
                </div>
                <div className="col my-auto">
                  <div>
                    <div className="fw-semibold">{item.name || "Untitled"}</div>
                    <div className="text-muted small">
                      {item.description || ""}
                    </div>
                  </div>
                </div>
                <div className="col my-auto">{formatLKR(item.price)}</div>
                <div className="col my-auto">
                  <div
                    className="d-inline-flex align-items-center border px-2 py-1"
                    style={{ borderRadius: "10px", backgroundColor: "#E1EDE3" }}
                  >
                    <button
                      className="btn btn-sm"
                      style={{ border: "none", fontSize: "16px" }}
                      onClick={() => dec(id)}
                      aria-label="Decrease quantity"
                    >
                      −
                    </button>
                    <span
                      className="px-3"
                      style={{
                        borderRadius: "10px",
                        backgroundColor: "#ffffffff",
                        fontSize: "16px",
                      }}
                    >
                      {item.qty ?? 1}
                    </span>
                    <button
                      className="btn btn-sm"
                      style={{ border: "none", fontSize: "16px" }}
                      onClick={() => inc(id)}
                      aria-label="Increase quantity"
                    >
                      +
                    </button>
                  </div>
                </div>
                <div className="col my-auto">{formatLKR(line)}</div>
                <div className="col-1 my-auto text-end">
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    style={{
                      fontSize: "16px",
                      borderRadius: "50%",
                      aspectRatio: "1/1",
                      padding: "0PX 8px",
                    }}
                    onClick={() => removeFromCart(id)}
                    aria-label="Remove item"
                  >
                    ✕
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Shipping estimator + summary */}
        <div className="row g-4">
          <div className="col-lg-7"></div>

          <div className="col-lg-5">
            <div
              className="p-4"
              style={{
                background: "#EEF1EA",
                borderRadius: 12,
              }}
            >
              <h5 className="mb-3">Estimate Shipping Cost</h5>

              <div className="mb-3">
                <label className="form-label small mb-1">Country</label>
                <select
                  className="form-select"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                >
                  <option value="">Select country</option>
                  <option value="Sri Lanka">Sri Lanka</option>
                  <option value="India">India</option>
                  <option value="United Kingdom">United Kingdom</option>
                  <option value="United States">United States</option>
                </select>
              </div>

              <div className="mb-3">
                <label className="form-label small mb-1">State/Province (City for quote)</label>
                <input
                  className="form-control"
                  placeholder="Enter city (e.g., Colombo)"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                />
              </div>

              <div className="mb-4">
                <label className="form-label small mb-1">Zip/Postal Code (optional)</label>
                <input
                  className="form-control"
                  placeholder="Enter zip/postal code"
                  value={zip}
                  onChange={(e) => setZip(e.target.value)}
                />
              </div>

              {shipError && (
                <div className="alert alert-warning py-2">{shipError}</div>
              )}
              {shippingRule && !shipError && (
                <div className="text-muted small mb-2">
                  Applied rule: <span className="fw-semibold">{shippingRule}</span>
                </div>
              )}

              <div className="d-flex justify-content-between py-1">
                <span className="text-muted">SUB TOTAL</span>
                <span>{formatLKR(subTotalAmount)}</span>
              </div>
              <div className="d-flex justify-content-between py-1">
                <span className="text-muted">
                  SHIPPING {shipLoading ? "(calculating…)" : ""}
                </span>
                <span>{formatLKR(shipping)}</span>
              </div>
              <hr />
              <div className="d-flex justify-content-between align-items-center">
                <span className="fw-semibold">TOTAL</span>
                <span className="fw-semibold">{formatLKR(grandTotal)}</span>
              </div>

              <div className="mt-3 d-grid">
                <Link className="nav-link" to="/checkout">
                 <button
                  className="btn btn-dark w-100"
                  disabled={shipLoading} >
                  Checkout
                </button>
                </Link>
               
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
